/**
 * KrpanoSpatialContext — Computes visible and focused hotspots
 * within the current camera viewport using angular distance math.
 *
 * This module is the "ground truth arbiter" in the Hybrid Pipeline:
 * it determines exactly which hotspots are visible in the current
 * frame using geometry (not AI), so the Vision LLM can narrate
 * based on confirmed object identity rather than guessing from pixels.
 *
 * Algorithm:
 *   1. Read camera state (hlookat, vlookat, fov) from krpano
 *   2. Enumerate all visible hotspots in the current scene
 *   3. For each hotspot, compute angular distance to camera center
 *   4. Filter hotspots within horizontal and vertical FOV bounds
 *   5. Sort by angular distance → nearest = focused_object
 */

import type { KrpanoType } from '@/types/krpano';
import { safeGetNumber, safeGetString, readCameraMetadata } from './krpano-canvas-capture';
import type { CameraMetadata } from './krpano-canvas-capture';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A hotspot that has been located within the viewport coordinate space. */
export interface SpatialHotspot {
  /** Unique hotspot name in krpano (e.g. "hs_123", "hotspot_456") */
  name: string;
  /** Database ID parsed from name pattern "hs_<id>" or "hotspot_<id>". Null if unparseable. */
  dbId: number | null;
  /** Azimuth angle in degrees (-180 to 180) */
  ath: number;
  /** Tilt angle in degrees (-90 to 90) */
  atv: number;
  /** Angular distance from viewport center in degrees (always >= 0) */
  angularDistanceFromCenter: number;
  /** Hotspot title/label if available */
  title: string;
  /** Hotspot type (e.g. "navigation", "info", "polygon") */
  type: string;
  /** Krpano style name — used to filter UI/overlay hotspots vs content */
  style: string;
}

/** Spatial context result for a single frame capture. */
export interface KrpanoSpatialContext {
  /** The hotspot nearest to viewport center — the "focused object" */
  focusedObject: SpatialHotspot | null;
  /** All hotspots within the viewport, sorted by angularDistance ascending */
  visibleObjects: SpatialHotspot[];
  /** Camera state at computation time */
  camera: {
    hlookat: number;
    vlookat: number;
    fov: number;   // horizontal FOV
    fov_v: number; // vertical FOV (computed from aspect ratio)
  };
  /** Current scene name */
  scene: string;
  /** Timestamp of computation (epoch ms) */
  computedAt: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Hotspot styles that are UI decorations, not content objects.
 * These are excluded from spatial context to avoid cluttering
 * the AI's object list with compass indicators, logos, etc.
 */
const EXCLUDED_STYLES = new Set([
  'hotspot_sylte',
  'hotspot_sylte_text',
  'logo',
  'nadirlogo',
  'compass',
  'compass2_plate',
]);

/**
 * Hotspot name prefixes that indicate UI/system hotspots,
 * not user-created content hotspots.
 */
const EXCLUDED_NAME_PREFIXES = [
  'skin_',
  'compass',
  'nadirlogo',
  'logo',
  'layer_thumbnail_', // Navigation thumbnail overlays
];

// ---------------------------------------------------------------------------
// Pure helper functions
// ---------------------------------------------------------------------------

/**
 * Compute the signed angular delta between two angles on a 360° circle.
 * Handles the -180 ↔ 180 wrap-around correctly.
 *
 * @returns Value in range (-180, 180]. Positive = target is to the right.
 *
 * Duplicated from iot-auto-callout.ts to avoid cross-domain coupling
 * (1-line pure math, justified exception to DRY).
 */
export function getSignedAngularDelta(targetAth: number, currentAth: number): number {
  return ((((targetAth - currentAth) + 180) % 360) + 360) % 360 - 180;
}

/**
 * Compute vertical FOV from horizontal FOV and viewport aspect ratio.
 *
 * Uses the standard perspective projection formula:
 *   fov_v = 2 * atan(tan(fov_h / 2) / aspectRatio)
 *
 * @param fovH Horizontal FOV in degrees
 * @param aspectRatio Width / Height (e.g. 16/9 = 1.778)
 * @returns Vertical FOV in degrees
 */
export function computeVerticalFov(fovH: number, aspectRatio: number): number {
  if (aspectRatio <= 0 || fovH <= 0) return fovH; // graceful fallback
  const halfFovHRad = (fovH / 2) * (Math.PI / 180);
  const halfFovVRad = Math.atan(Math.tan(halfFovHRad) / aspectRatio);
  return (halfFovVRad * 2 * 180) / Math.PI;
}

/**
 * Parse a database ID from a krpano hotspot name.
 * Recognizes patterns: "hs_123", "hotspot_123", "hs_123_invisible", "hs_123_svg", etc.
 *
 * @returns The numeric ID, or null if the name doesn't match a known pattern.
 */
export function parseHotspotDbId(name: string): number | null {
  // Match "hs_<digits>" or "hotspot_<digits>" at start, possibly followed by "_suffix"
  const match = name.match(/^(?:hs|hotspot)_(\d+)(?:_|$)/);
  if (match) {
    const id = parseInt(match[1], 10);
    return Number.isFinite(id) ? id : null;
  }
  return null;
}

/**
 * Check if a hotspot should be excluded from spatial context.
 * Excludes UI decorations, system overlays, and helper elements.
 */
export function isExcludedHotspot(name: string, style: string): boolean {
  if (EXCLUDED_STYLES.has(style)) return true;

  const nameLower = name.toLowerCase();
  for (const prefix of EXCLUDED_NAME_PREFIXES) {
    if (nameLower.startsWith(prefix)) return true;
  }

  // Exclude sub-hotspots (invisible labels, SVG overlays, traits, thumbnails, callout text)
  // These are children of a main hotspot and duplicate its position
  if (
    nameLower.endsWith('_invisible') ||
    nameLower.endsWith('_svg') ||
    nameLower.endsWith('_trait') ||
    nameLower.endsWith('_thumbnail') ||
    nameLower.endsWith('_callout_text')
  ) {
    return true;
  }

  return false;
}

// ---------------------------------------------------------------------------
// Krpano hotspot enumeration
// ---------------------------------------------------------------------------

/**
 * Read all hotspots currently loaded in krpano and return their spatial data.
 * Filters out hidden hotspots and UI decorations.
 */
export function enumerateKrpanoHotspots(krpano: KrpanoType): SpatialHotspot[] {
  const count = safeGetNumber(krpano, 'hotspot.count', 0);
  if (count <= 0) return [];

  const hotspots: SpatialHotspot[] = [];

  for (let i = 0; i < count; i++) {
    const name = safeGetString(krpano, `hotspot[${i}].name`);
    if (!name) continue;

    // Skip hidden hotspots — they're not on screen
    const visible = krpano.get(`hotspot[${i}].visible`);
    if (visible === false || visible === 'false') continue;

    const style = safeGetString(krpano, `hotspot[${i}].style`);

    // Skip UI/system hotspots
    if (isExcludedHotspot(name, style)) continue;

    // Skip navigation hotspots — these are links to other scenes, not objects/equipment.
    // Navigation hotspots have onclick handlers containing navigateSceneFromHotspot() or
    // fetchListByGroupId() calls. Including them pollutes the AI's object list with
    // scene names like "Phòng Khách" instead of actual equipment names.
    const onclick = safeGetString(krpano, `hotspot[${i}].onclick`);
    if (onclick && (
      onclick.includes('navigateSceneFromHotspot') ||
      onclick.includes('fetchListByGroupId') ||
      onclick.includes('window.open')
    )) {
      continue;
    }

    const ath = safeGetNumber(krpano, `hotspot[${i}].ath`);
    const atv = safeGetNumber(krpano, `hotspot[${i}].atv`);

    // Read title — krpano text hotspots may store it in 'tooltip' property
    let title = safeGetString(krpano, `hotspot[${i}].tooltip`);
    if (!title) {
      title = safeGetString(krpano, `hotspot[${i}].alt`);
    }
    // Fallback: try title_var (used by Heritage hotspot renderer)
    if (!title) {
      title = safeGetString(krpano, `hotspot[${i}].title_var`);
      // Strip HTML tags if present (title_var may contain <span> wrappers)
      if (title) {
        title = title.replace(/<[^>]*>/g, '').trim();
      }
    }

    const type = safeGetString(krpano, `hotspot[${i}].type`);

    hotspots.push({
      name,
      dbId: parseHotspotDbId(name),
      ath,
      atv,
      angularDistanceFromCenter: 0, // computed later in frustum filter
      title,
      type,
      style,
    });
  }

  return hotspots;
}

// ---------------------------------------------------------------------------
// Frustum filter
// ---------------------------------------------------------------------------

/**
 * Filter hotspots to only those within the camera viewport (frustum).
 * Computes angular distance from center for each visible hotspot.
 *
 * @param hotspots All candidate hotspots from the current scene
 * @param hlookat Camera horizontal look-at angle (degrees)
 * @param vlookat Camera vertical look-at angle (degrees)
 * @param fovH Horizontal field of view (degrees)
 * @param fovV Vertical field of view (degrees)
 * @returns Hotspots within viewport, sorted by angular distance ascending
 */
export function filterHotspotsInFrustum(
  hotspots: SpatialHotspot[],
  hlookat: number,
  vlookat: number,
  fovH: number,
  fovV: number,
): SpatialHotspot[] {
  if (fovH <= 0 || fovV <= 0) return [];

  const halfFovH = fovH / 2;
  const halfFovV = fovV / 2;
  const visible: SpatialHotspot[] = [];

  for (const hs of hotspots) {
    // Horizontal angular distance with wrap-around handling
    const deltaH = getSignedAngularDelta(hs.ath, hlookat);

    // Vertical angular distance (no wrap needed, atv is in [-90, 90])
    const deltaV = hs.atv - vlookat;

    // Check if within viewport bounds
    if (Math.abs(deltaH) <= halfFovH && Math.abs(deltaV) <= halfFovV) {
      // Euclidean angular distance to viewport center
      const angularDistance = Math.sqrt(deltaH * deltaH + deltaV * deltaV);

      visible.push({
        ...hs,
        angularDistanceFromCenter: angularDistance,
      });
    }
  }

  // Sort by angular distance — nearest to center first
  visible.sort((a, b) => a.angularDistanceFromCenter - b.angularDistanceFromCenter);

  return visible;
}

// ---------------------------------------------------------------------------
// Main API
// ---------------------------------------------------------------------------

/**
 * Get the viewport aspect ratio from krpano stage dimensions.
 * Falls back to window dimensions if krpano stage info is unavailable.
 */
export function getViewportAspectRatio(krpano: KrpanoType): number {
  // Try krpano stage dimensions first (accurate even when not fullscreen)
  const stageW = safeGetNumber(krpano, 'stagewidth', 0);
  const stageH = safeGetNumber(krpano, 'stageheight', 0);

  if (stageW > 0 && stageH > 0) {
    return stageW / stageH;
  }

  // Fallback to window dimensions
  if (typeof window !== 'undefined' && window.innerWidth > 0 && window.innerHeight > 0) {
    return window.innerWidth / window.innerHeight;
  }

  // Last resort: assume 16:9
  return 16 / 9;
}

/**
 * Compute the full spatial context for the current krpano viewport.
 *
 * This is the main entry point — call once per capture frame (~1fps).
 *
 * @param krpano The krpano instance (window.krpano)
 * @returns Spatial context with focused/visible objects, or null if krpano unavailable
 */
export function computeSpatialContext(krpano: KrpanoType | null): KrpanoSpatialContext | null {
  if (!krpano) return null;

  try {
    // 1. Read camera state
    const camera = readCameraMetadata(krpano);

    // 2. Compute vertical FOV from aspect ratio
    const aspectRatio = getViewportAspectRatio(krpano);
    const fovV = computeVerticalFov(camera.fov, aspectRatio);

    // 3. Enumerate all hotspots in current scene
    const allHotspots = enumerateKrpanoHotspots(krpano);

    // 4. Filter to those within viewport
    const visibleObjects = filterHotspotsInFrustum(
      allHotspots,
      camera.hlookat,
      camera.vlookat,
      camera.fov,
      fovV,
    );

    // 5. Focused object = nearest to center
    const focusedObject = visibleObjects.length > 0 ? visibleObjects[0] : null;

    return {
      focusedObject,
      visibleObjects,
      camera: {
        hlookat: camera.hlookat,
        vlookat: camera.vlookat,
        fov: camera.fov,
        fov_v: fovV,
      },
      scene: camera.scene,
      computedAt: Date.now(),
    };
  } catch (error) {
    console.warn('[KrpanoSpatialContext] Error computing spatial context:', error);
    return null;
  }
}
